import React, { FC } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GeneratorStepProps as Props, InstallationsFieldKeys, InstallationsInputsArray, InstallationTypes } from '../types'
import { ProducerContactSteps } from 'features/producer-contact/types'
import Button from 'components/button/Button'
import { ButtonTypes } from 'components/button/types'
import DateFormField from 'components/form-fields/date/DateFormField'
import { preventArrowUpAndDownEvent, preventInputScrollEvent } from 'utils/eventHandlers'
import FormButtonGroup from 'components/form-fields/form-button-group/FormButtonGroup'
import AddressForm from 'components/form-fields/address-section'
import classNames from 'classnames/bind'
import { installationLabels } from '../constants'
import Icon from 'components/icon/Icon'
import styles from './Installation.module.scss'
import { isValidEAN } from 'utils/helpers'

const Installations: FC<Props> = ({ formInput, handleNext, handlePrevious }) => {
  // i18n
  const { t } = useTranslation(['generator', 'validation'])

  // React hook form
  const hookForm = useForm<InstallationsInputsArray>({
    mode: 'onBlur',
    defaultValues: {
      installations: formInput?.[ProducerContactSteps.INSTALLATIONS] ?? [{}]
    }
  })
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues
  } = hookForm
  const { fields, append, remove } = useFieldArray({ name: 'installations', control })

  /**
   * Handles the submit next click
   *
   * @param {InstallationsInputsArray} data
   */
  const onSubmitNext = async (data: InstallationsInputsArray) => {
    handleNext(data.installations)
  }

  /**
   * Handles the submit previous click
   */
  const onSubmitPrevious = async () => {
    const data = getValues('installations')
    handlePrevious(data)
  }

  /**
   * Returns the key of the given field
   *
   * @param index
   * @param key
   */
  const getFieldKey = (index: number, key: InstallationsFieldKeys): string => {
    return `installations.${index}.${key}`
  }

  return (
    <form onSubmit={handleSubmit(onSubmitNext)}>
      <h3 className="margin-bottom-m">{t('progressInstallationData', 'Jouw installatie')}</h3>

      {fields.map((field, index) => {
        const watchType: InstallationTypes = watch(`installations.${index}.${InstallationsFieldKeys.INSTALLATION_TYPE}`)

        return (
          <fieldset key={field.id} className={styles.installation}>
            {index > 0 && (
              <div className="is-flex-row">
                <h4 className="margin-right-s">
                  {t('installationSubtitle', 'Installatie')} {index + 1}
                </h4>
                <span className={styles.delete} onClick={() => remove(index)} role="presentation">
                  <Icon name="trash" />
                </span>
              </div>
            )}
            <div className="form-row is-2">
              {/* INSTALLATION TYPE */}
              <div className="form-input">
                <label htmlFor={getFieldKey(index, InstallationsFieldKeys.INSTALLATION_TYPE)}>{t('installationType', 'Selecteer')}</label>
                <select
                  className={classNames({ 'has-error': errors?.installations?.[index]?.[InstallationsFieldKeys.INSTALLATION_TYPE] })}
                  {...register(`installations.${index}.${InstallationsFieldKeys.INSTALLATION_TYPE}`, { required: true })}
                >
                  <option value="">{t('select', { defaultValue: 'selecteer...' })}</option>
                  {Object.keys(installationLabels).map((type) => (
                    <option key={type} value={type}>
                      {t(installationLabels[type].i18nKey, installationLabels[type].default)}
                    </option>
                  ))}
                </select>
                {errors?.installations?.[index]?.[InstallationsFieldKeys.INSTALLATION_TYPE] && (
                  <p className="margin-top-s margin-bottom-none is-red">
                    {errors.installations[index][InstallationsFieldKeys.INSTALLATION_TYPE].type === 'required' && t('validation:required')}
                  </p>
                )}
              </div>

              {/* INSTALLATION EXPIRATION  */}
              {watchType === InstallationTypes.EXISTING && (
                <DateFormField
                  isFuture
                  isRequired
                  errors={errors?.installations?.[index]?.[InstallationsFieldKeys.EXPIRATION_CURRENT_CONTRACT]}
                  label={t('installationExpiration', 'Afloop huidig contract')}
                  hookFormSelector={getFieldKey(index, InstallationsFieldKeys.EXPIRATION_CURRENT_CONTRACT)}
                  {...hookForm}
                />
              )}
            </div>

            <div className="form-row is-2">
              {/* INSTALLED POWER */}
              <div className="form-input">
                <label htmlFor={getFieldKey(index, InstallationsFieldKeys.INSTALLED_POWER)}>
                  {t('installedPower', 'Geïnstalleerd vermogen')}
                </label>
                <div className="append-input">
                  <input
                    type="number"
                    className={classNames({ 'has-error': errors?.installations?.[index]?.[InstallationsFieldKeys.INSTALLED_POWER] })}
                    {...register(`installations.${index}.${InstallationsFieldKeys.INSTALLED_POWER}`, {
                      required: true,
                      validate: (value: number) => value >= 11
                    })}
                    onWheel={(e) => preventInputScrollEvent(e)}
                    onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
                  />
                  <div className="append">kWp</div>
                </div>
                {errors?.installations?.[index]?.[InstallationsFieldKeys.INSTALLED_POWER] && (
                  <p className="margin-top-s margin-bottom-none is-red">
                    {errors.installations[index][InstallationsFieldKeys.INSTALLED_POWER].type === 'required' && t('validation:required')}
                    {errors.installations[index][InstallationsFieldKeys.INSTALLED_POWER].type === 'validate' &&
                      t('validation:min.number', {
                        attribute: t('installedPower', 'Geïnstalleerd vermogen'),
                        min: 11
                      })}
                  </p>
                )}
              </div>

              {/* PRODUCED VOLUME */}
              <div className="form-input">
                <label htmlFor={getFieldKey(index, InstallationsFieldKeys.PRODUCED_VOLUME)}>
                  {t('producedVolume', 'Geproduceerd volume')}
                </label>
                <div className="append-input">
                  <input
                    type="number"
                    {...register(`installations.${index}.${InstallationsFieldKeys.PRODUCED_VOLUME}`)}
                    placeholder={t('optional', 'Optioneel')}
                    onWheel={(e) => preventInputScrollEvent(e)}
                    onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
                  />
                  <div className="append">MWh</div>
                </div>
              </div>
            </div>

            <div className="form-row is-2">
              {/* INJECTED VOLUME */}
              <div className="form-input">
                <label htmlFor={getFieldKey(index, InstallationsFieldKeys.INJECTED_VOLUME)}>
                  {t('injectedVolume', 'Geschat geïnjecteerd vermogen')}
                </label>
                <div className="append-input">
                  <input
                    type="number"
                    {...register(`installations.${index}.${InstallationsFieldKeys.INJECTED_VOLUME}`)}
                    placeholder={t('optional', 'Optioneel')}
                    onWheel={(e) => preventInputScrollEvent(e)}
                    onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
                  />
                  <div className="append">MWh</div>
                </div>
              </div>

              {/* EAN */}
              <div className="form-input">
                <label htmlFor={getFieldKey(index, InstallationsFieldKeys.EAN_INJECTION)}>
                  {t('eanInjection', 'EAN-nummer voor injectie')}
                </label>
                <input
                  type="text"
                  className={classNames({ 'has-error': errors?.installations?.[index]?.[InstallationsFieldKeys.EAN_INJECTION] })}
                  {...register(`installations.${index}.${InstallationsFieldKeys.EAN_INJECTION}`, {
                    validate: (value: number) => {
                      if (value) return isValidEAN(value.toString())
                      return true
                    }
                  })}
                  placeholder={t('optional', 'Optioneel')}
                />
                {errors?.installations?.[index]?.[InstallationsFieldKeys.EAN_INJECTION] && (
                  <p className="margin-top-s margin-bottom-none is-red">
                    {errors.installations[index][InstallationsFieldKeys.EAN_INJECTION].type === 'validate' &&
                      t('validation:ean', 'Dit is geen geldig EAN nummer')}
                  </p>
                )}
              </div>
            </div>

            <h4 className={'margin-bottom-m margin-top-l'}>{t('installationsAddressTitle', 'Adres van de installatie')}</h4>

            <div className="margin-bottom-l">
              {/* ADDRESS */}
              <AddressForm
                isNested
                input={formInput?.[ProducerContactSteps.INSTALLATIONS]?.[index]?.[InstallationsFieldKeys.INSTALLATION_ADDRESS]}
                type={`installations.${index}.${InstallationsFieldKeys.INSTALLATION_ADDRESS}`}
                className="margin-bottom-m"
                errors={errors?.installations?.[index]?.[InstallationsFieldKeys.INSTALLATION_ADDRESS]}
                {...hookForm}
              />
            </div>
          </fieldset>
        )
      })}

      <Button type={ButtonTypes.TERNARY} onClick={() => append({})} isNotSubmit>
        {t('addInstallation', 'Ik heb nog een installatie')}
      </Button>

      <FormButtonGroup>
        <Button type={ButtonTypes.TERNARY} onClick={onSubmitPrevious} isNotSubmit>
          {t('previousStep', 'Vorige stap')}
        </Button>

        <Button>{t('nextStep', 'Volgende stap')}</Button>
      </FormButtonGroup>
    </form>
  )
}

export default Installations
